class TrustBadge extends HTMLElement {
  #frame = 0;
  #pointerX = 0;
  #pointerY = 0;
  #scrollY = 0;
  #touchActive = false;
  #reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #resizeObserver = new ResizeObserver(() => this.#updateScale());

  connectedCallback() {
    this.addEventListener('pointermove', this.#onPointerMove);
    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('pointerup', this.#onPointerEnd);
    this.addEventListener('pointercancel', this.#onPointerEnd);
    this.addEventListener('pointerleave', this.#onPointerLeave);
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll, { passive: true });
    this.#resizeObserver.observe(this);
    this.#updateScale();
    this.#onScroll();
  }

  disconnectedCallback() {
    this.removeEventListener('pointermove', this.#onPointerMove);
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('pointerup', this.#onPointerEnd);
    this.removeEventListener('pointercancel', this.#onPointerEnd);
    this.removeEventListener('pointerleave', this.#onPointerLeave);
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
    this.#resizeObserver.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #updateScale = () => {
    const designWidth = Number(this.dataset.designWidth || 1100);
    const designHeight = Number(this.dataset.designHeight || 300);
    if (!designWidth || !designHeight) return;

    const scale = Math.min(1, this.clientWidth / designWidth);
    this.style.setProperty('--trust-scale', scale.toFixed(4));
    this.style.height = `${(designHeight * scale).toFixed(2)}px`;
  };

  #onPointerMove = (event) => {
    if (this.#reduceMotion.matches || (event.pointerType === 'touch' && !this.#touchActive)) return;
    const bounds = this.getBoundingClientRect();
    this.#pointerX = ((event.clientX - bounds.left) / bounds.width - 0.5) * 2;
    this.#pointerY = ((event.clientY - bounds.top) / bounds.height - 0.5) * 2;
    this.#render();
  };

  #onPointerDown = (event) => {
    if (this.#reduceMotion.matches || event.pointerType !== 'touch') return;
    this.#touchActive = true;
    this.setAttribute('data-touch-active', '');
    this.#onPointerMove(event);
  };

  #onPointerEnd = (event) => {
    if (event.pointerType !== 'touch') return;
    this.#touchActive = false;
    this.removeAttribute('data-touch-active');
    this.#pointerX = 0;
    this.#pointerY = 0;
    this.#render();
  };

  #onPointerLeave = (event) => {
    if (event.pointerType === 'touch') return;
    this.#pointerX = 0;
    this.#pointerY = 0;
    this.#render();
  };

  #onScroll = () => {
    if (this.#reduceMotion.matches || this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      const bounds = this.getBoundingClientRect();
      const viewportCenter = window.innerHeight / 2;
      const badgeCenter = bounds.top + bounds.height / 2;
      this.#scrollY = Math.max(-1, Math.min(1, (viewportCenter - badgeCenter) / viewportCenter));
      this.#render();
    });
  };

  #render() {
    const strength = Number(this.dataset.parallaxStrength || 12);
    const x = this.#pointerX * strength;
    const y = (this.#pointerY + this.#scrollY * 0.55) * strength;

    this.style.setProperty('--trust-parallax-x', `${x.toFixed(2)}px`);
    this.style.setProperty('--trust-parallax-y', `${y.toFixed(2)}px`);
    this.style.setProperty('--trust-rotate-x', `${(-this.#pointerY * 7).toFixed(2)}deg`);
    this.style.setProperty('--trust-rotate-y', `${(this.#pointerX * 7).toFixed(2)}deg`);
    this.style.setProperty('--trust-light-x', `${((this.#pointerX + 1) * 50).toFixed(1)}%`);
    this.style.setProperty('--trust-light-y', `${((this.#pointerY + 1) * 50).toFixed(1)}%`);
    this.style.setProperty('--trust-seal-scroll-y', `${(this.#scrollY * 8).toFixed(2)}px`);
  }
}

if (!customElements.get('trust-badge')) {
  customElements.define('trust-badge', TrustBadge);
}
